
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import FbxQrScanner from '@/components/fbx-qr-scanner.vue';
import SvgXMark from '@/components/Icons/SvgXMark.vue';
import VirtualRedTagInvalidQr from '@/views/VirtualRedTag/VirtualRedTagInvalidQr.vue';
import CameraBegging from '@/components/CameraBegging.vue';
import queries from '@/queries';
import { asTrailerOrNull } from '@/shared/trailerLookup';
import { ActionTypes } from '@/store/actionTypes';
import useCameraPermission from '@/shared/composables/useCameraPermission';
import usePermissionAlert from '@/shared/composables/usePermissionAlert';
import checkVirtualRedTagEligibility from '@/shared/mixins/checkVirtualRedTagEligibility';
import { TrailerSelectionMethod } from '@/types/TrailerSelectionMethod';
import {
    extractScannedTrailerId,
    isTrailerSolutionsQrUrl,
} from '@/shared/trailerQrScan';

export default defineComponent({
    name: 'VirtualRedTagScanQr',
    components: {
        FbxQrScanner,
        SvgXMark,
        VirtualRedTagInvalidQr,
        CameraBegging,
    },
    mixins: [checkVirtualRedTagEligibility],
    setup() {
        const { isCameraPermissionGranted, setIsCameraShared } =
            useCameraPermission();
        const { setPermissionAlert } = usePermissionAlert();

        return {
            isCameraPermissionGranted,
            setIsCameraShared,
            setPermissionAlert,
        };
    },
    data(): {
        isScannerReady: boolean;
        scannerError: boolean;
        displayScanFailedOverlay: boolean;
        isLookingUpTrailer: boolean;
        showCameraBegging: boolean;
    } {
        return {
            isScannerReady: false,
            scannerError: false,
            displayScanFailedOverlay: false,
            isLookingUpTrailer: false,
            showCameraBegging: true,
        };
    },
    computed: {
        ...mapGetters({
            driver: 'getDriver',
        }),
        driverCompany(): string {
            return this.$route.params.company as string;
        },
        actionsEnabled(): boolean {
            return this.isScannerReady || this.scannerError;
        },
        isPaused(): boolean {
            return this.displayScanFailedOverlay || this.isLookingUpTrailer;
        },
        cameraBeggingEnabled(): boolean {
            return this.showCameraBegging && !this.isCameraPermissionGranted;
        },
    },
    methods: {
        navigateToWelcome(): void {
            this.$router.push({ name: 'VirtualRedTagEntry' });
        },
        navigateToManualEntry(): void {
            this.$router.push({ name: 'VirtualRedTagManualEntry' });
        },
        onScannerReady(isReady: boolean): void {
            this.setIsCameraShared(true);
            this.isScannerReady = isReady;
        },
        onScannerError(): void {
            this.setIsCameraShared(false);
            this.setPermissionAlert();
            this.scannerError = true;
        },
        async onScanned(qrUrl: URL): Promise<void> {
            if (!isTrailerSolutionsQrUrl(qrUrl)) {
                this.displayScanFailedOverlay = true;
                return;
            }

            const scannedId = extractScannedTrailerId(qrUrl);

            this.isLookingUpTrailer = true;
            const trailer = asTrailerOrNull(
                await queries.getTrailerByQrId(scannedId, false)
            );

            if (!trailer) {
                this.isLookingUpTrailer = false;
                this.displayScanFailedOverlay = true;
                return;
            }

            await this.$store.dispatch(ActionTypes.SET_SELECTED_TRAILER, {
                trailer,
                trailerSelectionMethod: TrailerSelectionMethod.qrScan,
            });

            const canRedTag = await this.checkVirtualRedTagEligibility({
                trailerNumber: trailer.trailerNumber,
                trailerCompany: trailer.company,
                // Tells the backend this was a real scan of the code on the trailer,
                // which is itself proof the driver is standing at it.
                scannedQrId: scannedId,
                driverCompany: this.driverCompany,
                driverId: this.driver?.id,
            });
            this.isLookingUpTrailer = false;

            // A network/API failure means we never actually got an answer, so it can't
            // be read as "ineligible". Fall back to VIN/plate verification, the same
            // soft degrade the old attach-flow check used for its own failures, rather
            // than dead-ending the driver on a transient blip.
            if (this.virtualRedTagEligibilityCheckFailed) {
                this.$router.push({
                    name: 'VirtualRedTagVerifyTrailer',
                    params: { trailerNumber: trailer.trailerNumber },
                    query: { trailerCompany: trailer.company },
                });
                return;
            }

            if (!canRedTag) {
                this.$router.push({
                    name: 'VirtualRedTagUnavailable',
                    params: { trailerNumber: trailer.trailerNumber },
                    query: {
                        reason:
                            this.virtualRedTagIneligibilityReason ?? undefined,
                        trailerCompany: trailer.company,
                    },
                });
                return;
            }

            // A scan normally proves proximity on the backend, but if it could not be
            // confirmed the driver still has to verify by VIN or plate.
            this.$router.push({
                name: this.virtualRedTagRequiresIdentityVerification
                    ? 'VirtualRedTagVerifyTrailer'
                    : 'VirtualRedTagConfirmation',
                params: { trailerNumber: trailer.trailerNumber },
                query: { trailerCompany: trailer.company },
            });
        },
    },
});
